import React, { useState, useEffect } from 'react';
import api from '../api';

const fieldSets = {
  flight: [
    { name: 'departureCity', label: 'Departure City', type: 'text', required: true },
    { name: 'destination', label: 'Destination', type: 'text', required: true },
    { name: 'travelDate', label: 'Travel Date', type: 'date', required: true },
    { name: 'passengers', label: 'Number of Passengers', type: 'number', required: true }
  ],
  visa: [
    { name: 'nationality', label: 'Nationality', type: 'text', required: true },
    { name: 'destinationCountry', label: 'Destination Country', type: 'text', required: true },
    { name: 'travelDate', label: 'Travel Date', type: 'date', required: true }
  ],
  hotel: [
    { name: 'destinationCity', label: 'Destination City', type: 'text', required: true },
    { name: 'checkInDate', label: 'Check-in Date', type: 'date', required: true },
    { name: 'checkOutDate', label: 'Check-out Date', type: 'date', required: true },
    { name: 'guests', label: 'Number of Guests', type: 'number', required: true }
  ],
  holiday: [
    { name: 'destination', label: 'Destination', type: 'text', required: true },
    { name: 'travelDates', label: 'Travel Dates', type: 'text', required: true, placeholder: 'e.g. 12-18 Dec 2026' },
    { name: 'budget', label: 'Budget (approx.)', type: 'text', required: false }
  ],
  general: [
    { name: 'message', label: 'Tell us about your travel plans', type: 'textarea', required: true }
  ]
};

const typeLabels = {
  flight: 'Flight Inquiry',
  visa: 'Visa Assistance Inquiry',
  hotel: 'Hotel Booking Inquiry',
  holiday: 'Holiday Package Inquiry',
  general: 'General Inquiry'
};

export default function InquiryForm({ defaultType = 'general', compact = false }) {
  const [type, setType] = useState(defaultType);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [status, setStatus] = useState(null); // null | 'submitting' | 'success' | 'error'
  // Sync internal type and clear form when the parent changes defaultType
  useEffect(() => {
    setType(defaultType);
    setForm({ name: '', phone: '', email: '' });
    setStatus(null);
    setErrorMsg('');
  }, [defaultType]);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setForm({ name: form.name, phone: form.phone, email: form.email });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      await api.post('/inquiries', { type, ...form });
      setStatus('success');
      setForm({ name: '', phone: '', email: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card" style={{ padding: compact ? 20 : 32 }}>
      {!compact && <h3>{typeLabels[type]}</h3>}

      <div className="form-field" style={{ marginBottom: 16 }}>
        <label htmlFor="inquiry-type">I'm interested in</label>
        <select id="inquiry-type" value={type} onChange={handleTypeChange}>
          <option value="flight">Flight Booking</option>
          <option value="visa">Visa Assistance</option>
          <option value="hotel">Hotel Booking</option>
          <option value="holiday">Holiday Package</option>
          <option value="general">Something else</option>
        </select>
      </div>

      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="name">Full Name</label>
          <input id="name" name="name" required value={form.name} onChange={handleChange} />
        </div>
        <div className="form-field">
          <label htmlFor="phone">Phone Number</label>
          <input id="phone" name="phone" type="tel" required value={form.phone} onChange={handleChange} />
        </div>
        <div className="form-field" style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="email">Email</label>
          <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} />
        </div>

        <div className="form-field" style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="description">Tell us more about your inquiry</label>
          <textarea
            id="description"
            name="description"
            rows={4}
            placeholder="Share any additional details, preferences, or questions you have..."
            value={form.description || ''}
            onChange={handleChange}
          />
        </div>

        {fieldSets[type].map((f) => (
          <div className="form-field" key={f.name} style={f.type === 'textarea' ? { gridColumn: '1 / -1' } : undefined}>
            <label htmlFor={f.name}>{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea id={f.name} name={f.name} rows={4} required={f.required} placeholder={f.placeholder} value={form[f.name] || ''} onChange={handleChange} />
            ) : (
              <input id={f.name} name={f.name} type={f.type} required={f.required} placeholder={f.placeholder} value={form[f.name] || ''} onChange={handleChange} />
            )}
          </div>
        ))}
      </div>

      <button type="submit" className="btn btn-primary" style={{ marginTop: 18, width: '100%' }} disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending…' : 'Submit Inquiry'}
      </button>

      {status === 'success' && (
        <p className="form-message success">Thanks! We've received your request and sent a confirmation to your email — our team will be in touch shortly.</p>
      )}
      {status === 'error' && <p className="form-message error">{errorMsg}</p>}
    </form>
  );
}
